import { onUnmounted, Ref } from "vue";
import { useEventListener } from "@vueuse/core";

export function useTooltip(
    elRef: Ref<HTMLElement | { $el: HTMLElement } | null | undefined>,
    onShow: (showing: Promise<void>) => void,
    delay = 300,
): void {
    let isHovering = false;

    // iOS(Androidも？)では、要素をタップした直後に(おせっかいで)mouseoverイベントを発火させたりするため、それを無視するためのフラグ
    // 無視しないと、画面に触れてないのにツールチップが出たりし、ユーザビリティが損なわれる
    // TODO: 一度でもタップすると二度とマウスでツールチップ出せなくなるのをどうにかする 定期的にfalseに戻すとか...？
    let shouldIgnoreMouseover = false;

    let timeoutId: number;

    const disposeStates: Array<() => void> = [];

    const open = () => {
        close();
        if (!isHovering) return;
        if (elRef.value == null) return;
        const el =
            elRef.value instanceof Element ? elRef.value : elRef.value.$el;
        if (!document.body.contains(el)) return; // openしようとしたときに既に元要素がDOMから消えている場合があるため

        const { promise, resolve } = Promise.withResolvers<void>();

        onShow(promise);
        disposeStates.push(resolve);
    };

    const close = () => {
        disposeStates.forEach((resolve) => resolve());
        disposeStates.splice(0);
    };

    const onMouseover = () => {
        if (isHovering) return;
        if (shouldIgnoreMouseover) return;
        isHovering = true;
        timeoutId = window.setTimeout(open, delay);
    };

    const onMouseleave = () => {
        if (!isHovering) return;
        isHovering = false;
        window.clearTimeout(timeoutId);
        close();
    };

    const onTouchstart = () => {
        shouldIgnoreMouseover = true;
        if (isHovering) return;
        isHovering = true;
        timeoutId = window.setTimeout(open, delay);
    };

    const onTouchend = () => {
        if (!isHovering) return;
        isHovering = false;
        window.clearTimeout(timeoutId);
        close();
    };

    useEventListener(elRef, "mouseover", onMouseover, { passive: true });
    useEventListener(elRef, "mouseleave", onMouseleave, { passive: true });
    useEventListener(elRef, "touchstart", onTouchstart, { passive: true });
    useEventListener(elRef, "touchend", onTouchend, { passive: true });
    useEventListener(elRef, "click", close, { passive: true });

    onUnmounted(() => {
        close();
    });
}
